/**
 * Split a verbatim transcript into customer/agent turns.
 *
 * Recognises `Customer:` and `Agent:` prefixes (case-insensitive) on each
 * paragraph (paragraphs are separated by blank lines).
 *
 * If the verbatim contains NO recognised speaker prefix anywhere, the
 * parser falls back to a single turn containing the verbatim text
 * exactly as supplied (preserving paragraph breaks, no synthetic
 * "customer" turn splits). This covers real-world records that aren't
 * transcripts at all (social-media posts, single-paragraph complaints,
 * pasted free-form prose) — the UI still renders the full text rather
 * than guessing at turn boundaries that aren't there.
 *
 * Per `feature-spec.md` §3.4.1 the left pane shows "parsed verbatim broken
 * into customer/agent turns".
 */

export interface VerbatimTurn {
  /**
   * `'none'` is used when the verbatim isn't a transcript at all (social
   * post, free-form prose, single paragraph). The rendering pane uses
   * unstyled prose for `'none'` instead of a speech-bubble — labelling
   * non-transcript text as "customer" was misleading.
   */
  speaker: 'customer' | 'agent' | 'none';
  text: string;
}

const PREFIX_RE = /^(customer|agent)\s*:\s*/i;
// Anywhere-in-the-text variant: matches `Customer:` / `Agent:` at the
// start of any line (after a newline or at string start). If this
// doesn't match the verbatim has no transcript shape and we should
// render it raw.
const ANY_PREFIX_RE = /(^|\n)\s*(customer|agent)\s*:/i;

export const parseVerbatimTurns = (verbatim: string): VerbatimTurn[] => {
  if (!verbatim || !verbatim.trim()) return [];

  // No recognised speaker prefix → render the whole string as a
  // single unstyled block (`speaker: 'none'`). Avoids the false-
  // positive "customer said:" framing on social posts, single-
  // paragraph complaints, and other non-transcript prose.
  if (!ANY_PREFIX_RE.test(verbatim)) {
    return [{ speaker: 'none', text: verbatim.trim() }];
  }

  const blocks = verbatim.split(/\n\s*\n/);
  const turns: VerbatimTurn[] = [];
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    const m = trimmed.match(PREFIX_RE);
    if (m) {
      turns.push({
        speaker: m[1].toLowerCase() as 'customer' | 'agent',
        text: trimmed.replace(PREFIX_RE, '').trim(),
      });
    } else {
      // Mixed transcript with an unlabelled paragraph (e.g. a
      // continuation): treat it as a customer turn so the pane still
      // renders the text. The whole-string fallback above already
      // handles the all-unlabelled case.
      turns.push({ speaker: 'customer', text: trimmed });
    }
  }
  return turns;
};
